function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import parseSpreadsheetInfo from './parseSpreadsheetInfo.js';
import parseFilePaths from './parseFilePaths.js';
import parseStyles from './parseStyles.js';
import parseSharedStrings from './parseSharedStrings.js';
import parseSheet from './parseSheet.js';
import convertValuesFromUint8ArraysToStrings from '../utility/convertValuesFromUint8ArraysToStrings.js';
import checkpoint from '../utility/checkpoint.js';
import isPromise from '../utility/isPromise.js';
import InvalidSpreadsheetError from './InvalidSpreadsheetError.js';
import SheetNotFoundError from './SheetNotFoundError.js';

// These exports are only used in `worker-f` dependencies:
// import parseXmlStream '../xml/parseXmlStream.js'
// import { decodeUtf8String, strFromU8 } from '../utility/convertValuesFromUint8ArraysToStrings.js'
// import parseExcelDate from './parseExcelDate.js'
// import parseExcelTimestamp from './parseExcelTimestamp.js'
// import isDateFormat, { DATE_FORMAT_SPECIFIC_LOCALE_PREFIX, DATE_FORMAT_ALLOW_ANY_OTHER_TEXT_SUFFIX, IS_DATE_FORMAT_CACHE, DATE_TEMPLATE_TOKENS } from './isDateFormat.js'
// import isDateFormatStyle, { BUILT_IN_DATE_FORMAT_IDS } from './isDateFormatStyle.js'
// import parseCell, { EMPTY_CELL } from './parseCell.js'
// import parseCellAddress, { LETTERS } from './parseCellAddress.js'
// import convertArrayOfCellsTo2dArrayOfValues from './convertArrayOfCellsTo2dArrayOfValues.js'
// import dropEmptyTrailingRows from './dropEmptyTrailingRows.js'
// import dropEmptyTrailingColumns from './dropEmptyTrailingColumns.js'
// import { latestCheckpointTimestamp } from '../utility/checkpoint.js'

// `worker-f` is not used because passing cells data from the worker thread
// to the main thread was a lengthy-enough operation. For example, when running
// the benchmark, the output latency was: `10` ms on "1mb.xlsx", `70` ms on "10mb.xlsx"
// and `500` ms on "50mb.xlsx", meaning that it would still have issues with
// blocking the main thread when passing the result back to it from the worker thread,
// negating the effect of using `worker-f` in the first place.
//
// As a way to work around the slow "structured clone" algorithm for input/output data,
// I played around with implementing a `serializeCells()` function that would serialize
// sheet data into an `ArrayBuffer` in order for it to be "transferred" instantly
// to the main thread, but this serialization itself was even slower, meaning that it would
// still block the main thread when deserializing that `ArrayBuffer` back to sheet data,
// so the entire idea of using `worker-f` was eventually dismissed.
//
// Instead of that, I submitted a PR in `saxen` repo that added "streaming mode" to the XML parser.
// https://github.com/nikku/saxen/pull/27
// That PR got merged, so a simple alternative now is to parse sheet data chunk-by-chunk,
// spacing it out with `setTimeout(0)` interrupts.
//
// In case of re-enabling `worker-f`, uncomment the relevant code above and below,
// and also replace `import { Parser } from 'saxen'` with an copy-paste of `saxen`'s code
// in `parseXmlStream.saxen.js`, otherwise it'll throw: "Parser is not defined".
// Also, in `convertValuesFromUint8ArraysToStrings.js`, replace `import { strFromU8 } from 'fflate'`
// with a copy-paste of the `strFromU8()` function code from `fflate`'s repo,
// because otherwise it'd throw: "td is not defined".
//
var CAN_USE_WORKER = false;

/**
 * Reads data from an `.xlsx` file.
 * @param  {function} parseXml — SAX XML parser.
 * @param  {Record<string,Uint8Array>} contents - A map of `.xml` files inside the `.xlsx` file (which itself is just a zipped directory).
 * @param  {object} [options]
 * @return {Promise<Sheet[]>}
 */
function parseSpreadsheetContents(parseXml, contents_) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // For an introduction in reading `.xlsx` files see "The minimum viable XLSX reader":
  // https://www.brendanlong.com/the-minimum-viable-xlsx-reader.html

  // Convert the values in `contents_` from `Uint8Array`s to `string`s.
  //
  // This function is a bit of a bottleneck on large `.xlsx` files.
  // For example, when running the benchmark, the time of calling this function is:
  //
  // * "1mb.xlsx" — 2
  // * "10mb.xlsx" — 7
  // * "50mb.xlsx" — 35
  //
  // When running this code in a worker, it's no longer a "bottleneck"
  // because in that case it doesn't block the main thread.
  //
  var contents = convertValuesFromUint8ArraysToStrings(contents_);

  // Because of how `.xlsx` file contents are defined in the specification,
  // it will have to be read in 3 passes:
  // * First pass — read the actual file paths
  // * Second pass — read "shared strings" and "styles"
  // * Thirs pass — read the sheets data

  checkpoint('parse spreadsheet info and file paths');

  // Get spreadsheet info and the paths to files.
  return readFiles(getXmlFilesAtFixedPaths(), contents, parseXml).then(function (_ref) {
    var spreadsheetInfo = _ref.spreadsheetInfo,
      filePaths = _ref.filePaths;
    checkpoint('parse "shared strings" and "styles"');

    // Parse "shared strings" and "styles".
    return readFiles(getXmlFilesAtNonFixedPaths(filePaths), contents, parseXml).then(function (_ref2) {
      var sharedStrings = _ref2.sharedStrings,
        styles = _ref2.styles;
      var sheetRelationIdsToRead = options.sheets ? options.sheets.map(function (sheet) {
        return getSheetRelationId(sheet, spreadsheetInfo.sheets);
      }) : spreadsheetInfo.sheets.map(function (_) {
        return _.relationId;
      });
      checkpoint("parse sheet".concat(sheetRelationIdsToRead.length === 1 ? '' : 's', " data"));

      // Parse sheets data.
      return readFiles(getSheetDataXmlFiles(filePaths, sheetRelationIdsToRead, {
        sharedStrings: sharedStrings,
        styles: styles,
        epoch1904: spreadsheetInfo.epoch1904,
        options: options
      }), contents, parseXml).then(function (sheetsData) {
        checkpoint('end');
        // Return sheets data.
        return sheetRelationIdsToRead.map(function (sheetRelationId) {
          return {
            sheet: getSheetNameByRelationId(sheetRelationId, spreadsheetInfo.sheets),
            data: sheetsData[sheetRelationId]
          };
        });
      });
    });
  });
}

/**
 * Reads data from an `.xlsx` file in a worker.
 * @param  {function} [createWorkerFunction] — Creates a worker function.
 * @param  {function} parseXml — SAX XML parser.
 * @param  {Record<string,Uint8Array>} contents - A map of `.xml` files inside the `.xlsx` file (which itself is just a zipped directory).
 * @param  {object} [options]
 * @return {Promise<Sheet[]>}
 */
export default function parseSpreadsheetContentsInWorker(createWorkerFunction, parseXml, contents, options) {
  // Assign a default value of `null` to `parseNumber()` function in the `options`.
  // The reason is that the worker code requires it to be non-`undefined`.
  // Otherwise, it would throw "parseNumber is not defined".
  if (!(options && options.parseNumber)) {
    options = _objectSpread(_objectSpread({}, options), {}, {
      parseNumber: null
    });
  }

  // If the environment doesn't support "workers", parse spreadsheet contents "synchronously".
  // This will "block" the main thread while parsing.
  if (!createWorkerFunction || !CAN_USE_WORKER) {
    return parseSpreadsheetContents(parseXml, contents, options);
  }

  // // Any functions have to be removed from the `options` in order for them to be "serializable"
  // // before sending them to the worker thread.
  // const { parseNumber: parseNumber_, ...optionsJson } = options
  //
  // // Create a worker from a function.
  // const workerFn = createWorkerFunction(
  //   (data) => {
  //     // Reconstruct the `options`.
  //     const options = {
  //       ...data.optionsJson,
  //       parseNumber: parseNumber_
  //     }
  //     // Parse sheet data from the `.xml` files.
  //     return parseSpreadsheetContents(parseXml, data.contents, options)
  //   }
  // )
  //
  // workerFn.addDependencies(
  //   // Any "outside" dependencies that're referenced from the function body.
  //   () => [
  //     parseXml,
  //     parseXmlStream,
  //     parseNumber,
  //     parseNumber_,
  //     parseExcelDate,
  //     parseExcelTimestamp,
  //     parseCell,
  //     EMPTY_CELL,
  //     parseCellAddress,
  //     LETTERS,
  //     convertArrayOfCellsTo2dArrayOfValues,
  //     convertValuesFromUint8ArraysToStrings,
  //     strFromU8,
  //     decodeUtf8String,
  //     dropEmptyTrailingRows,
  //     dropEmptyTrailingColumns,
  //     isDateFormat,
  //     DATE_FORMAT_SPECIFIC_LOCALE_PREFIX,
  //     DATE_FORMAT_ALLOW_ANY_OTHER_TEXT_SUFFIX,
  //     IS_DATE_FORMAT_CACHE, DATE_TEMPLATE_TOKENS,
  //     isDateFormatStyle,
  //     BUILT_IN_DATE_FORMAT_IDS,
  //     parseSpreadsheetContents,
  //     parseFilePaths,
  //     parseSpreadsheetInfo,
  //     parseSharedStrings,
  //     parseStyles,
  //     parseSheet,
  //     getSheetRelationId,
  //     getSheetNameByRelationId,
  //     getXmlFilesAtFixedPaths,
  //     getXmlFilesAtNonFixedPaths,
  //     getSheetDataXmlFiles,
  //     readFiles,
  //     checkpoint,
  //     latestCheckpointTimestamp,
  //     isPromise
  //   ]
  // )
  //
  // workerFn.inputTransferList(({ optionsJson, contents }) => Object.keys(contents).map(key => contents[key].buffer))
  //
  // return workerFn.callOnce({ optionsJson, contents }).then((result) => {
  //   console.log('~ Input latency', workerFn.inputLatency)
  //   console.log('~ Output latency', workerFn.outputLatency)
  //   return result
  // })
}

function getSheetRelationId(sheet, sheets) {
  if (typeof sheet === 'string') {
    for (var _iterator = _createForOfIteratorHelperLoose(sheets), _step; !(_step = _iterator()).done;) {
      var _sheet = _step.value;
      if (_sheet.name === sheet) {
        return _sheet.relationId;
      }
    }
    throw new SheetNotFoundError("Sheet \"".concat(sheet, "\" not found. Available sheets: ").concat(sheets.map(function (_ref3) {
      var name = _ref3.name;
      return "\"".concat(name, "\"");
    }).join(', ')));
  } else {
    if (sheet <= sheets.length) {
      return sheets[sheet - 1].relationId;
    }
    throw new SheetNotFoundError("Sheet number out of bounds: ".concat(sheet, ". Available sheets count: ").concat(sheets.length));
  }
}
function getSheetNameByRelationId(sheetRelationId, sheets) {
  for (var _iterator2 = _createForOfIteratorHelperLoose(sheets), _step2; !(_step2 = _iterator2()).done;) {
    var sheet = _step2.value;
    if (sheet.relationId === sheetRelationId) {
      return sheet.name;
    }
  }
  // The only way of getting `sheetRelationId` here is from the `sheets`,
  // so this error is not technically possible. And if it is thrown
  // then it means that there's a bug in the code because it's not
  // supposed to get `sheetRelationId` from anywhere other than the `sheets`.
  throw new Error("Sheet relation ID not found: ".concat(sheetRelationId));
}
function getXmlFilesAtFixedPaths() {
  return {
    // Read the paths to certain files inside the `.xlsx` file, which is itself just a `.zip` archive.
    // These paths aren't standardized between different spreadsheet editors.
    // https://github.com/tidyverse/readxl/issues/104
    'xl/_rels/workbook.xml.rels': {
      name: 'filePaths',
      parse: parseFilePaths
    },
    // General info on the spreadsheet.
    'xl/workbook.xml': {
      name: 'spreadsheetInfo',
      parse: parseSpreadsheetInfo
    }
  };
}
function getXmlFilesAtNonFixedPaths(filePaths) {
  var _ref4;
  return _ref4 = {}, _defineProperty(_ref4, filePaths.sharedStrings || 'xl/sharedStrings.xml', {
    name: 'sharedStrings',
    // `parseSharedStrings()` returns a `Promise`.
    parse: parseSharedStrings,
    // It seems that "sharedStrings.xml" is not required to exist.
    // For example, that could be the case when a spreadsheet doesn't contain any strings.
    // https://github.com/catamphetamine/read-excel-file/issues/85
    fallback: []
  }), _defineProperty(_ref4, filePaths.styles || 'xl/styles.xml', {
    name: 'styles',
    parse: parseStyles,
    fallback: {}
  }), _ref4;
}

// Returns the list of sheet data `.xml` files.
function getSheetDataXmlFiles(filePaths, sheetRelationIdsToRead, sheetDataParserParameters) {
  return Object.keys(filePaths.sheets).filter(function (sheetRelationId) {
    return sheetRelationIdsToRead.includes(sheetRelationId);
  }).reduce(function (filesInfo, sheetRelationId) {
    return _objectSpread(_objectSpread({}, filesInfo), {}, _defineProperty({}, filePaths.sheets[sheetRelationId], {
      name: sheetRelationId,
      // `parseSheet()` returns a `Promise`.
      parse: function parse(content, parseXml) {
        return parseSheet(content, parseXml, sheetDataParserParameters);
      }
    }));
  }, {});
}

// In case of converting `.zip` file reader from a "read-and-return" one to a "streaming" one,
// this function could be modified to process the files as they come rather than all-at-once.

// Reads files from inside an `.xlsx` archive by file paths.
//
// In case of converting `.zip` file reader from a "read-and-return" one to a "streaming" one,
// this function could be modified to process the files as they come rather than all-at-once.
//
// But there's a catch: inside an `.xlsx` file, some file paths are not fixed
// and are instead defined in "xl/_rels/workbook.xml.rels" file,
// which presents a "chicken and an egg" dilemma: how could one possibly
// read an `.xlsx` file in one go when the order of the files inside it isn't fixed
// and could be random. Most likely, in the majority of cases, "xl/_rels/workbook.xml.rels"
// file is gonna be one of the first in a given `.xlsx` archive, but still it's not guaranteed.
// A solution would be reading an `.xlsx` file in two passes: one pass would be just to read
// the "xl/_rels/workbook.xml.rels" and ignore decompressing anything else,
// and then the second pass would be to read all other files whose paths are now known.
//
// Returns:
// * If none of the `parse()` functions returned a `Promise`, it returns a map of files' contents.
// * If any of the `parse()` functions returned a `Promise`, it returns a `Promise` that resolves to a map of files' contents.
//
function readFiles(filesInfo, contents, parseXml) {
  // Get files' contents.
  var results = {};
  var _loop = function _loop() {
    var filePath = _Object$keys[_i];
    var fileInfo = filesInfo[filePath];
    results[fileInfo.name] = contents[filePath] === undefined ? fileInfo.fallback === undefined ? function () {
      throw new InvalidSpreadsheetError("\"".concat(filePath, "\" file not found inside the `.xlsx` file"));
    }() : fileInfo.fallback : fileInfo.parse(contents[filePath], parseXml);
  };
  for (var _i = 0, _Object$keys = Object.keys(filesInfo); _i < _Object$keys.length; _i++) {
    _loop();
  }
  // Resolve any `Promise`s.
  var promises = [];
  var _loop2 = function _loop2() {
    var name = _Object$keys2[_i2];
    if (isPromise(results[name])) {
      promises.push(results[name].then(function (result) {
        results[name] = result;
      }));
    }
  };
  for (var _i2 = 0, _Object$keys2 = Object.keys(results); _i2 < _Object$keys2.length; _i2++) {
    _loop2();
  }
  if (promises.length > 0) {
    return Promise.all(promises).then(function () {
      return results;
    });
  }
  return results;
}
//# sourceMappingURL=parseSpreadsheetContents.js.map